import { Link, Outlet, createRootRoute, createRoute, createRouter } from '@tanstack/react-router';

// ---------------------------------------------------------------------------
// Host detection — the prospect-facing intake lives on the `submit.` subdomain
// (submit.klevaideas.com), the agent dashboard on submitpro.klevaideas.com.
// Both serve the same SPA bundle, so we branch on hostname at runtime.
// ("submitpro." does NOT start with "submit." — the dot disambiguates.)
// ---------------------------------------------------------------------------

function isPublicIntakeHost(): boolean {
  if (typeof window === 'undefined') return false;
  return window.location.hostname.startsWith('submit.');
}

// ---------------------------------------------------------------------------
// Root layout — app shell with top nav bar
// ---------------------------------------------------------------------------

function RootLayout() {
  // Public intake is standalone & full-screen — public.tsx renders its own
  // header, so skip the agent nav shell entirely on the prospect domain.
  if (isPublicIntakeHost()) {
    return <Outlet />;
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Top nav bar */}
      <header className="sticky top-0 z-50 border-b border-border bg-surface/95 backdrop-blur supports-[backdrop-filter]:bg-surface/80">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-6">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-navy text-white font-heading font-bold text-sm">
              SP
            </div>
            <span className="font-heading text-lg font-semibold text-navy">SubmitPro</span>
          </Link>

          {/* Nav links */}
          <nav className="flex items-center gap-1">
            <Link
              to="/dashboard"
              className="rounded-lg px-3.5 py-2 text-sm font-medium text-text-secondary transition-colors hover:bg-surface-sunken hover:text-text-primary [&.active]:bg-blue/10 [&.active]:text-blue"
            >
              Pipeline
            </Link>
            <Link
              to="/intake"
              className="rounded-lg px-3.5 py-2 text-sm font-medium text-text-secondary transition-colors hover:bg-surface-sunken hover:text-text-primary [&.active]:bg-blue/10 [&.active]:text-blue"
            >
              New Submission
            </Link>
            <div className="ml-3 h-8 w-px bg-border" />
            <button
              type="button"
              className="ml-3 flex h-9 w-9 items-center justify-center rounded-full bg-navy/10 text-navy text-sm font-semibold transition-colors hover:bg-navy/20"
              title="Account"
            >
              SK
            </button>
          </nav>
        </div>
      </header>

      {/* Main content */}
      <main>
        <Outlet />
      </main>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Route tree
// ---------------------------------------------------------------------------

const rootRoute = createRootRoute({
  component: RootLayout,
});

// Lazy imports for route components
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: () => {
    // On the prospect domain, the root IS the intake form.
    if (isPublicIntakeHost()) {
      return <PublicIntakePage />;
    }
    // Otherwise: agent landing
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-navy text-white font-heading font-bold text-2xl">
            SP
          </div>
          <h1 className="font-heading text-2xl font-bold text-navy mb-2">Welcome to SubmitPro</h1>
          <p className="text-text-secondary mb-6 max-w-md">
            Streamline your commercial insurance submissions with intelligent intake forms and
            pipeline management.
          </p>
          <div className="flex items-center justify-center gap-3">
            <Link
              to="/dashboard"
              className="rounded-lg bg-navy px-5 py-2.5 text-sm font-medium text-white shadow-sm transition-colors hover:bg-navy-light"
            >
              View Pipeline
            </Link>
            <Link
              to="/intake"
              className="rounded-lg border border-border bg-surface px-5 py-2.5 text-sm font-medium text-text-primary shadow-sm transition-colors hover:bg-surface-sunken"
            >
              New Submission
            </Link>
          </div>
        </div>
      </div>
    );
  },
});

// Import route components
import DashboardPage from './dashboard';
import IntakePage from './intake/index';
import PublicIntakePage from './intake/public';
import { LoginPage } from './login';

const dashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dashboard',
  component: DashboardPage,
});

const intakeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/intake',
  component: IntakePage,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
});

// Public intake — standalone, full-screen (no nav shell from root)
// We render it inside the root layout but the component itself handles full-screen
const publicIntakeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/submit/$tenantSlug',
  component: PublicIntakePage,
});

export const routeTree = rootRoute.addChildren([
  indexRoute,
  dashboardRoute,
  intakeRoute,
  loginRoute,
  publicIntakeRoute,
]);
